import React, { useMemo } from 'react';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, Cell
} from 'recharts';
import { FaChartBar, FaChartLine, FaChartPie } from 'react-icons/fa';

/**
 * Componente para exibir visualizações gráficas das estatísticas do usuário
 */
const StatsCharts = ({ history }) => {
  const [activeChart, setActiveChart] = React.useState('performance');
  
  // Preparar dados para o gráfico de desempenho (linha)
  const performanceData = useMemo(() => {
    if (!history || history.length === 0) return [];
    
    // Ordenar histórico por timestamp/epoch
    const sortedHistory = [...history].sort((a, b) => {
      const aEpoch = parseInt(a.epoch);
      const bEpoch = parseInt(b.epoch);
      return aEpoch - bEpoch;
    });
    
    // Calcular lucro/perda acumulado
    let cumulativeProfit = 0;
    return sortedHistory.map(entry => {
      const betAmount = parseFloat(entry.amount);
      const winAmount = entry.won ? parseFloat(entry.payout) : 0;
      const profit = winAmount - betAmount;
      cumulativeProfit += profit;
      
      return {
        epoch: entry.epoch,
        profit,
        cumulativeProfit: parseFloat(cumulativeProfit.toFixed(2)),
        position: entry.position,
        won: entry.won
      };
    });
  }, [history]);
  
  // Preparar dados para o gráfico de distribuição de apostas (barras)
  const betDistributionData = useMemo(() => {
    if (!history || history.length === 0) return [];
    
    // Agrupar apostas por posição e resultado
    const distribution = {
      upWon: history.filter(entry => entry.position === 'up' && entry.won).length,
      upLost: history.filter(entry => entry.position === 'up' && !entry.won).length,
      downWon: history.filter(entry => entry.position === 'down' && entry.won).length,
      downLost: history.filter(entry => entry.position === 'down' && !entry.won).length
    };
    
    return [
      { name: 'UP', won: distribution.upWon, lost: distribution.upLost },
      { name: 'DOWN', won: distribution.downWon, lost: distribution.downLost }
    ];
  }, [history]);
  
  // Preparar dados para o gráfico de resultados (pizza)
  const outcomeData = useMemo(() => {
    if (!history || history.length === 0) return [];
    
    const wins = history.filter(entry => entry.won).length;
    const losses = history.length - wins;
    
    return [
      { name: 'Wins', value: wins, color: '#10b981' },
      { name: 'Losses', value: losses, color: '#ef4444' }
    ];
  }, [history]);
  
  // Cores para os gráficos
  const COLORS = {
    up: '#10b981',
    down: '#ef4444',
    profit: '#10b981',
    loss: '#ef4444',
    neutral: '#7900ff'
  };
  
  // Renderizar gráfico de desempenho (linha)
  const renderPerformanceChart = () => (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={performanceData}
          margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
          <XAxis 
            dataKey="epoch" 
            tick={{ fill: '#9ca3af' }} 
            label={{ value: 'Round', position: 'insideBottom', fill: '#9ca3af' }}
          />
          <YAxis 
            tick={{ fill: '#9ca3af' }} 
            label={{ value: 'USDT', angle: -90, position: 'insideLeft', fill: '#9ca3af' }}
          />
          <Tooltip 
            contentStyle={{ backgroundColor: '#1f2937', borderColor: '#374151', color: '#fff' }}
            formatter={(value) => [`${value} USDT`, undefined]}
          />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="cumulativeProfit" 
            name="Cumulative Profit/Loss" 
            stroke="#7900ff" 
            strokeWidth={2}
            dot={{ fill: '#7900ff', r: 4 }}
            activeDot={{ r: 6, fill: '#a855f7' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
  
  // Renderizar gráfico de distribuição de apostas (barras)
  const renderBetDistributionChart = () => (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={betDistributionData}
          margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
          <XAxis dataKey="name" tick={{ fill: '#9ca3af' }} />
          <YAxis tick={{ fill: '#9ca3af' }} />
          <Tooltip 
            contentStyle={{ backgroundColor: '#1f2937', borderColor: '#374151', color: '#fff' }}
          />
          <Legend />
          <Bar dataKey="won" name="Won" fill={COLORS.up} />
          <Bar dataKey="lost" name="Lost" fill={COLORS.down} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
  
  // Renderizar gráfico de resultados (pizza)
  const renderOutcomeChart = () => (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={outcomeData}
            cx="50%"
            cy="50%"
            labelLine={false}
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
            nameKey="name"
            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
          >
            {outcomeData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip 
            contentStyle={{ backgroundColor: '#1f2937', borderColor: '#374151', color: '#fff' }}
          />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
  
  // Renderizar o gráfico ativo
  const renderActiveChart = () => {
    switch (activeChart) {
      case 'performance':
        return renderPerformanceChart();
      case 'distribution':
        return renderBetDistributionChart();
      case 'outcome':
        return renderOutcomeChart();
      default:
        return renderPerformanceChart();
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-sm text-gray-400">Performance Charts</h4>
        
        <div className="flex gap-2">
          <button
            onClick={() => setActiveChart('performance')}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${
              activeChart === 'performance'
                ? 'bg-[#7900ff] text-white'
                : 'bg-[#7900ff]/20 text-gray-400 hover:bg-[#7900ff]/30'
            }`}
          >
            <FaChartLine size={10} />
            Profit
          </button>
          
          <button
            onClick={() => setActiveChart('distribution')}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${
              activeChart === 'distribution'
                ? 'bg-[#7900ff] text-white'
                : 'bg-[#7900ff]/20 text-gray-400 hover:bg-[#7900ff]/30'
            }`}
          >
            <FaChartBar size={10} />
            Bets
          </button>
          
          <button
            onClick={() => setActiveChart('outcome')}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${
              activeChart === 'outcome'
                ? 'bg-[#7900ff] text-white'
                : 'bg-[#7900ff]/20 text-gray-400 hover:bg-[#7900ff]/30'
            }`}
          >
            <FaChartPie size={10} />
            Results
          </button>
        </div>
      </div>
      
      {/* Área do gráfico */}
      <div className="bg-[#0d0d0d] rounded-lg p-3">
        {history.length > 0 ? (
          renderActiveChart()
        ) : (
          <div className="h-64 flex items-center justify-center">
            <p className="text-gray-500">No data available for selected filters</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatsCharts;
